<template>
    <div id="fiche-bu-monthly-news-block" class="background-primary-lighter">
        <div class="background-primary flex text-center">
            <div class="col-12">
                <as-title class="color-white" :title="$t('ficheBu.detail.monthly_news_block.title')"
                    :size="4"></as-title>
                <as-title v-if="lastRitualDates.length === 1" class="color-white ml-2"
                    :title="'(' + getRitualMonthTitle(lastRitualDates[0]) + ')'" :size="4"></as-title>
            </div>
        </div>
        <template v-if="pdfBeingGenerated">
            <div class="w-100">
                <div v-for="(monthData, mIndex) in pdfMonths" :key="'month-' + mIndex" class="mb-4">
                    <div class="background-primary">
                        <as-title class="p-2 m-0 color-white" :title="getRitualMonthTitle(monthData.date)"
                            :size="4"></as-title>
                    </div>
                    <div class="flex">
                        <div class="col-6 p-0">
                            <template v-for="(sujet, i) in monthData.leftCol" :key="'left-' + i">
                                <FicheBuMonthlyNewsBlockRitualDetailsSubject :pdfBeingGenerated="pdfBeingGenerated"
                                    :sujet="sujet" :index="monthData.subjectIndexes[sujet.id]" :defaultOpen="true"
                                    @toggle="handleToggle" />
                            </template>
                        </div>
                        <div class="col-6 p-0">
                            <template v-for="(sujet, i) in monthData.rightCol" :key="'right-' + i">
                                <FicheBuMonthlyNewsBlockRitualDetailsSubject :pdfBeingGenerated="pdfBeingGenerated"
                                    :sujet="sujet" :index="monthData.subjectIndexes[sujet.id]" :defaultOpen="true"
                                    @toggle="handleToggle" />
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else-if="lastRitualDates.length > 1">
            <TabView>
                <TabPanel v-for="(date, index) in lastRitualDates" :key="index">
                    <template #header>
                        <as-title class="color-white" :title="getRitualMonthTitle(date)" :size="4"></as-title>
                    </template>
                    <FicheBuMonthlyNewsBlockRitualDetailsSubject v-for="(sujet, sujetIndex) in getRitualSubjects(date)"
                        :key="sujet.id" :sujet="sujet" :index="sujetIndex"
                        :defaultOpen="openedIndex === 'all' || openedIndex === sujetIndex" @toggle="handleToggle" />
                </TabPanel>
            </TabView>
        </template>
        <template v-else>
            <template v-if="!this.ritualSubjects || !this.ritualSubjects.length">
                <div class="mt-3 mb-3">
                    <div v-if="isBusy" class="h-100 flex align-items-center">
                        <ProgressBar mode="indeterminate" style="height: 3px; max-width: 75%;" class="flex-1 m-auto">
                        </ProgressBar>
                    </div>
                    <template v-else>
                        <div class="flex flex-column h-100 align-items-center text-center">
                            <as-title class="mt-auto" :title="$t('ficheBu.detail.no_data_message')"
                                :size="6"></as-title>
                            <img :src="noDataImage" class="ml-auto mr-auto mb-auto" />
                        </div>
                    </template>
                </div>
            </template>
            <div v-else id="fiche-bu-monthly-news-block__content" class="flex w-100">
                <FicheBuMonthlyNewsBlockRitualDetailsSubject v-for="(sujet, sujetIndex) in ritualSubjects"
                    :key="sujet.id" :sujet="sujet" :index="sujetIndex"
                    :defaultOpen="openedIndex === 'all' || openedIndex === sujetIndex" @toggle="handleToggle" />
            </div>
        </template>
    </div>
</template>

<script>
import AsTitle from '../Elements/AsTitle.vue';
import FicheBuMonthlyNewsBlockRitualDetailsSubject from './FicheBuMonthlyNewsBlockRitualDetailsSubject.vue';
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";

export default {
    name: "FicheBuMonthlyNewsBlock",
    components: {
        AsTitle,
        FicheBuMonthlyNewsBlockRitualDetailsSubject,
        TabView,
        TabPanel,
    },
    props: {
        ritualSubjects: {
            type: Array,
            required: true
        },
        lastRitualDates: {
            type: Array,
            required: true
        },
        pdfBeingGenerated: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            noDataImage: new URL("../../assets/images/icons/no-data-warning.png", import.meta.url).href,
            openedIndex: 0,
            previousOpenedIndex: null,
            selectedRitualDate: null,
        };
    },
    computed: {
        locale() {
            return this.$i18n.locale;
        },
        pdfMonths() {
            if (!this.pdfBeingGenerated) return [];

            return this.lastRitualDates.map(date => {
                const subjects = this.getRitualSubjects(date);
                const subjectElements = [];
                const subjectIndexes = {};
                let totalHeight = 0;

                subjects.forEach((sujet, index) => {
                    subjectIndexes[sujet.id] = index;
                    let h = 80;
                    if (sujet.note) {
                        const text = sujet.note.toString();
                        const blockTagsCount = (text.match(/<(p|br|li|h[1-6]|div|table|img)[^>]*>/gi) || []).length;
                        const textLength = text.replace(/<[^>]*>?/gm, '').trim().length;
                        const charLines = Math.ceil(textLength / 80);
                        const totalLines = charLines + blockTagsCount;
                        h += Math.max(20, totalLines * 24);
                    }
                    h += 20;

                    subjectElements.push({ sujet, height: h });
                    totalHeight += h;
                });

                let bestSplitIndex = 0;
                let minDiff = Infinity;
                let currentLeftHeight = 0;

                for (let i = 0; i < subjectElements.length; i++) {
                    currentLeftHeight += subjectElements[i].height;
                    let currentRightHeight = totalHeight - currentLeftHeight;
                    let diff = Math.abs(currentLeftHeight - currentRightHeight);

                    if (diff <= minDiff) {
                        minDiff = diff;
                        bestSplitIndex = i;
                    }
                }

                // Pour équilibrer si tout est d'un seul côté, forcer index = length - 1 si c'est le cas et qu'il y a plus d'1 élément.
                if (subjectElements.length > 1 && bestSplitIndex === subjectElements.length - 1) {
                    bestSplitIndex = subjectElements.length - 2;
                }

                const leftCol = [];
                const rightCol = [];

                subjectElements.forEach((item, index) => {
                    if (index <= bestSplitIndex) {
                        leftCol.push(item.sujet);
                    } else {
                        rightCol.push(item.sujet);
                    }
                });

                return {
                    date,
                    leftCol,
                    rightCol,
                    subjectIndexes
                };
            });
        }
    },
    methods: {
        getRitualMonthTitle(dateStr) {
            const [id, year, month] = dateStr.split('-');
            const date = new Date(Number(year), Number(month) - 1);
            const monthName = date.toLocaleString(this.locale, { month: 'long' });
            const formattedMonth =
                this.locale === 'fr'
                    ? monthName.charAt(0).toLowerCase() + monthName.slice(1)
                    : monthName.charAt(0).toUpperCase() + monthName.slice(1);

            return `${formattedMonth} ${year}`;
        },
        getRitualSubjects(ritualDate) {
            const [id] = ritualDate.split('-');
            return this.ritualSubjects.filter(sujet => sujet.idRituel === +id);
        },
        handleToggle(index, isCollapsed) {
            if (!isCollapsed) {
                this.openedIndex = index;
                this.$nextTick(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                });
            } else {
                this.openedIndex = null;
            }
        },

        openAllPanels() {
            this.previousOpenedIndex = this.openedIndex;
            this.openedIndex = 'all';
        },

        restorePreviousState() {
            this.openedIndex = this.previousOpenedIndex;
        },
    }
};
</script>

<style lang="scss">
@import "../../assets/styles/colors.scss";

#fiche-bu-monthly-news-block {
    &__content {
        align-items: stretch;
    }

    .p-tabview-nav {
        display: flex;
        background-color: $primary_color;
        gap: 10px;
        border-width: 0;
    }

    .p-tabview-header {
        flex: 1;
    }

    .p-tabview-header a {
        font-weight: unset;
        background-color: $dark_blue_color;
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
        border-bottom-width: 0px;
    }

    .p-tabview-header.p-highlight a {
        background-color: $primary_light_color;
    }

    .p-tabview-header a>* {
        margin: auto;
    }

    .p-tabview-panels {
        padding: 0;
    }
}
</style>
